import { cva } from 'class-variance-authority';
import type { ComponentProps, JSX, ReactNode, Ref } from 'react';

export const typographyVariants = cva('', {
    defaultVariants: {
        variant: 'div',
    },
    variants: {
        center: {
            true: 'text-center',
        },
        color: {
            primary: 'text-text-dark',
            secondary: 'text-text-secondary',
            grey: 'text-text-grey',
            subscription: 'text-text-subscription',
            link: 'text-text-link',
            blockquote: 'text-text-blockquote',
            light: 'text-text-light',
            'accent-cold': 'text-text-accent-cold',
            'accent-warm': 'text-text-accent-warm',
        },
        variant: {
            h1: [
                'm-0 font-bold text-[28px] leading-[1.25]!',
                'mobile:text-[52px] mobile:leading-[1.2]!',
                'lg-tablet:text-[64px] lg-tablet:leading-[1.2]!',
            ],
            h2: [
                'm-0 font-bold text-[24px] leading-[1.4]!',
                'mobile:text-[40px] mobile:leading-[1.2]!',
                'lg-tablet:text-[48px] lg-tablet:leading-[1.2]!',
            ],
            h3: [
                'm-0 font-semibold text-[20px] leading-[1.2]!',
                'mobile:text-[20px] mobile:leading-[1.2]!',
                'lg-tablet:text-[30px] lg-tablet:leading-[1.5]!',
            ],
            h4: '',
            h5: '',
            h6: '',
            p: [
                'm-0!',
                'font-normal text-[18px] leading-[1.5]!',
                'mobile:text-[20px]',
                'lg-tablet:text-[22px]',
            ],
            aside: [
                'm-0!',
                'font-normal text-[18px] leading-[1.5]!',
                'mobile:text-[20px]',
                'lg-tablet:text-[24px]',
            ],
            span: '',
            div: 'font-normal mobile:text-[22px] text-[14px] leading-[140%]! mobile:leading-[150%]!',
        },
    },
});

type TypographyProps = {
    center?: boolean;
    children: ReactNode;
    className?: string;
    color?:
        | 'primary'
        | 'secondary'
        | 'grey'
        | 'subscription'
        | 'link'
        | 'blockquote'
        | 'light'
        | 'accent-cold'
        | 'accent-warm';

    ref?: Ref<HTMLDivElement>;
    variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'aside' | 'div';
} & ComponentProps<'div'>;

export function Typography({
    center,
    children,
    className = '',
    color = 'primary',
    ref,
    variant = 'div',
    ...props
}: TypographyProps) {
    const Component: keyof JSX.IntrinsicElements = variant;

    return (
        <Component
            ref={ref}
            {...props}
            className={typographyVariants({ center, className, color, variant })}
        >
            {children}
        </Component>
    );
}

Typography.displayName = 'Typography';
